import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/app/store/auth'
import { Plus, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { LocationFormDialog } from '../components/LocationFormDialog'
import { LocationDeleteDialog } from '../components/LocationDeleteDialog'
import { LocationViewDialog } from '../components/LocationViewDialog'
// Google Maps dialog kept for reference
// import { LocationMapDialog } from '../components/LocationMapDialog'
import { GlobalMap } from '@/components/maps/GlobalMap'
import { toMapLocations, toMapVaults, toVaultCopies, type ApiMapNode, type ApiMapConnection } from '@/components/maps/mapAdapters'
import { useLocations, useDeleteLocation, useLocationMapData } from '../hooks'
import { createLocationColumns } from '../columns'
import { useCan } from '@/hooks/useCan'
import type { Location } from '@/types/entities'

export function LocationListPage() {
    const { t } = useTranslation()
    const [selectedLocation, setSelectedLocation] = useState<Location | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)
    // The screen is open to every locations.read; a change is offered only where the API allows it.
    const canCreate = useCan('locations.create')
    const canUpdate = useCan('locations.update')
    const canDelete = useCan('locations.delete')

    const { data: locationsResponse, isLoading } = useLocations()
    const deleteLocation = useDeleteLocation()
    const { data: locationMapRaw = [] } = useLocationMapData(true)
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

    // Fetch paths data
    const { data: pathsData } = useQuery({
        queryKey: ['vault-paths'],
        queryFn: async () => {
            const res = await (await import('@/lib/api/axios')).default.get('/api/vaults/paths')
            const d = (res.data as any)?.data
            return d as { locations: ApiMapNode[]; connections: ApiMapConnection[] }
        },
        enabled: isAuthenticated,
    })

    // Extract locations array from paginated response
    const locations: Location[] = useMemo(() => {
        const dataArray = locationsResponse?.data
        if (!dataArray || !Array.isArray(dataArray)) {
            return []
        }
        return dataArray
    }, [locationsResponse])

    /**
     * Locations for the map, INCLUDING the ones with no coordinates.
     *
     * This used to filter them out, which is why the map looked complete while ~44% of the
     * estate by object count was missing from it. GlobalMap plots what it can and counts the
     * rest in its footer.
     */
    const mapLocations = useMemo(
        () => toMapLocations((locationMapRaw as ApiMapNode[]).map((l: any) => ({
            // MUST match the paths endpoint's key, which is `loc-{Location.id}`. Keying on
            // the description meant every copy edge failed its endpoint lookup and was
            // skipped — silently, by design, which is what hid it.
            id: l.id != null ? `loc-${l.id}` : (l.description || l.city),
            name: l.description || l.city || 'Unknown',
            type: 'dc' as const,
            latitude: l.lat,
            longitude: l.lng,
            address: [l.address1, l.address2, l.county, l.postcode, l.city, l.country].filter(Boolean).join(', '),
            category: l.categoryName,
            objectCount: l.objectCount,
            timeZone: l.timeZone,
        }))),
        [locationMapRaw]
    )

    // The paths payload carries both ends and the edges between them.
    const pathVaults = useMemo(() => toMapVaults(pathsData?.locations as ApiMapNode[] | undefined), [pathsData])
    /** Edges are off by default: on a busy estate they crowd the markers. */
    const [showEdges, setShowEdges] = useState(false)

    const pathCopies = useMemo(() => toVaultCopies(pathsData?.connections), [pathsData])

    // Memoize callbacks to prevent re-creating on every render
    const handleEdit = useCallback((location: Location) => {
        setSelectedLocation(location)
        setShowEditDialog(true)
    }, [])

    const handleDelete = useCallback((location: Location) => {
        setSelectedLocation(location)
        setShowDeleteDialog(true)
    }, [])

    const handleView = useCallback((location: Location) => {
        setSelectedLocation(location)
        setShowViewDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedLocation) {
            deleteLocation.mutate(selectedLocation.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedLocation(null)
                },
            })
        }
    }, [selectedLocation, deleteLocation])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export locations to CSV')
    }, [])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createLocationColumns(canDelete ? handleDelete : undefined, canUpdate ? handleEdit : undefined, handleView, t),
        [handleDelete, handleEdit, handleView, canUpdate, canDelete, t]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('locations.loadingLocations')}</div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">{t('locations.title')}</h1>
                    <p className="text-muted-foreground">
                        {t('locations.pageDescription')}
                    </p>
                </div>
                {canCreate && (
                    <div className="flex space-x-2">
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('locations.createLocation')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Map */}
            <div className="flex items-center justify-end">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <input
                        type="checkbox"
                        checked={showEdges}
                        onChange={(e) => setShowEdges(e.target.checked)}
                        className="h-3.5 w-3.5 cursor-pointer accent-primary"
                    />
                    {t('maps.showEdges', 'Show vault copies')}
                </label>
            </div>
            <GlobalMap
                locations={mapLocations}
                vaults={pathVaults}
                copies={showEdges ? pathCopies : []}
                height={360}
            />

            {/* Stats */}
            <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-card p-6 md:col-start-2">
                    <div className="flex items-center space-x-2">
                        <MapPin className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('locations.totalLocations')}</p>
                            <p className="text-2xl font-bold">{locations.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <DataTable
                columns={columns}
                data={locations}
                searchKey="description"
                searchPlaceholder={t('locations.searchPlaceholder')}
                onExport={handleExport}
                exportButtonText={t('locations.export')}
                enableRowSelection={false}
            />

            {/* Dialogs */}
            <LocationFormDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                location={null}
            />

            <LocationFormDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                location={selectedLocation}
            />

            <LocationDeleteDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                location={selectedLocation}
                onConfirm={handleDeleteConfirm}
                isLoading={deleteLocation.isPending}
            />

            <LocationViewDialog
                open={showViewDialog}
                onOpenChange={setShowViewDialog}
                location={selectedLocation}
            />

        </div>
    )
}

